import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, List, ListItem,
    ListItemText, ListItemSecondaryAction, IconButton,
    Divider, CircularProgress, Avatar, Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import ShareLedgerDialog from './ShareLedgerDialog';
import { useTranslation } from 'react-i18next';

const ManageWorkspaceDialog = ({ open, onClose, ledgers, onRefresh }) => {
    const { t } = useTranslation();
    const { api } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    
    // Sharing state
    const [shareOpen, setShareOpen] = useState(false);
    const [selectedForShare, setSelectedForShare] = useState(null);

    // Delete Confirmation state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [ledgerToDelete, setLedgerToDelete] = useState(null);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setIsSubmitting(true);
        try {
            const response = await api.post('/ledger/ledgers', { name: newName });
            const newLedgerId = response.data.id;
            setNewName('');
            setIsCreating(false);
            onRefresh();
            showToast('Book created successfully', 'success');
            
            // Navigate to the new ledger and close dialog
            navigate(`/ledger/${newLedgerId}/dashboard`);
            onClose();
        } catch (error) {
            const detail = error.response?.data?.detail || error.message || 'Failed to create book';
            showToast(detail, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdate = async (id) => {
        if (!editName.trim()) return;
        setIsSubmitting(true);
        try {
            await api.put(`/ledger/ledgers/${id}`, { name: editName });
            setEditingId(null);
            onRefresh();
            showToast('Book renamed', 'success');
        } catch (error) {
            const detail = error.response?.data?.detail || error.message || 'Failed to rename book';
            showToast(detail, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenDelete = (ledger) => {
        setLedgerToDelete(ledger);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!ledgerToDelete) return;
        setDeletingId(ledgerToDelete.id);
        try {
            await api.delete(`/ledger/ledgers/${ledgerToDelete.id}`);
            showToast(`Book "${ledgerToDelete.name}" deleted successfully`, 'success');
            setDeleteDialogOpen(false);
            onRefresh();

            // If user is currently on the deleted ledger page, redirect
            const currentPath = window.location.pathname;
            if (currentPath.includes(`/ledger/${ledgerToDelete.id}`)) {
                const remaining = ledgers.filter(l => l.id !== ledgerToDelete.id);
                if (remaining.length > 0) {
                    navigate(`/ledger/${remaining[0].id}/dashboard`);
                } else {
                    navigate('/');
                }
            }
            setLedgerToDelete(null);
        } catch (error) {
            console.error("Failed to delete ledger", error);
            const detail = error.response?.data?.detail || error.message || 'Failed to delete book';
            showToast(detail, 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const handleOpenShare = (ledger) => {
        setSelectedForShare(ledger);
        setShareOpen(true);
    };

    return (
        <>
            <Dialog open={open} onClose={isSubmitting || deletingId !== null ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: 'background.default' } }}>
                <DialogTitle sx={{ fontWeight: 800, mt: 1, color: 'primary.main', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {t("Manage Books")}
                    {!isCreating && (
                        <Button 
                            startIcon={<AddIcon />} 
                            variant="contained" 
                            size="small" 
                            onClick={() => setIsCreating(true)}
                            disabled={isSubmitting || deletingId !== null}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            {t("New Book")}
                        </Button>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {t("Organize your architecture. Create, rename, or share your books.")}
                    </Typography>

                    {isCreating && (
                        <Box sx={{ mb: 4, p: 2, borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04), border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.08)}` }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'primary.main' }}>{t("Create New Book")}</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    size="small"
                                    placeholder="e.g. Zenith Heights"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    sx={{ bgcolor: '#fff' }}
                                />
                                <Button 
                                    variant="contained" 
                                    onClick={handleCreate}
                                    disabled={isSubmitting || !newName.trim()}
                                >
                                    {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t('Create')}
                                </Button>
                                <IconButton onClick={() => setIsCreating(false)} size="small" disabled={isSubmitting} aria-label={t("Close")}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    )}

                    <List sx={{ width: '100%' }}>
                        {ledgers.map((ledger, index) => (
                            <React.Fragment key={ledger.id}>
                                <ListItem sx={{ py: 2, px: 1 }}>
                                    <Avatar sx={{ mr: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), color: 'primary.main', width: 40, height: 40, borderRadius: 1 }}>
                                        <AccountBalanceWalletIcon fontSize="small" />
                                    </Avatar>
                                    
                                    {editingId === ledger.id ? (
                                        <Box sx={{ display: 'flex', flexGrow: 1, gap: 1 }}>
                                            <TextField
                                                autoFocus
                                                fullWidth
                                                size="small"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                disabled={isSubmitting}
                                            />
                                            <IconButton onClick={() => handleUpdate(ledger.id)} color="primary" disabled={isSubmitting || !editName.trim()}>
                                                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                            </IconButton>
                                            <IconButton onClick={() => setEditingId(null)} disabled={isSubmitting}>
                                                <CloseIcon />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <ListItemText
                                            primary={ledger.name}
                                            primaryTypographyProps={{ fontWeight: 700, color: 'primary.main' }}
                                            secondary={ledger.role === 'admin' ? t('Owner') : t('Shared with you')}
                                        />
                                    )}

                                    {editingId !== ledger.id && (
                                        <ListItemSecondaryAction>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title={t("Share")}>
                                                    <IconButton size="small" onClick={() => handleOpenShare(ledger)} disabled={isSubmitting || deletingId !== null} aria-label={t("Share")}>
                                                        <ShareIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {ledger.role === 'admin' && (
                                                    <>
                                                        <Tooltip title={t("Rename")}>
                                                            <IconButton size="small" onClick={() => { setEditingId(ledger.id); setEditName(ledger.name); }} disabled={isSubmitting || deletingId !== null} aria-label={t("Rename")}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={ledgers.length <= 1 ? t("Cannot delete your last book") : t("Delete")}>
                                                            <span>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error" 
                                                                    disabled={deletingId === ledger.id || ledgers.length <= 1 || isSubmitting} 
                                                                    onClick={() => handleOpenDelete(ledger)}
                                                                    aria-label={t("Delete")}
                                                                >
                                                                    {deletingId === ledger.id ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </Box>
                                        </ListItemSecondaryAction>
                                    )}
                                </ListItem>
                                {index < ledgers.length - 1 && <Divider component="li" sx={{ opacity: 0.5 }} />}
                            </React.Fragment>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={onClose} disabled={isSubmitting || deletingId !== null} sx={{ fontWeight: 700, px: 3 }}>{t("Done")}</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Book Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={deletingId !== null ? undefined : () => { setDeleteDialogOpen(false); setLedgerToDelete(null); }}
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: 'error.main' }}>
                    {t("Delete Book?")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 1.5 }}>
                        {t("Are you sure you want to delete")} <strong>{ledgerToDelete?.name}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("This will permanently remove all transactions, cashflow forecasts, annual budgets, accounts, and investment data associated with this book. This action cannot be undone.")}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => { setDeleteDialogOpen(false); setLedgerToDelete(null); }}
                        disabled={deletingId !== null}
                        sx={{ fontWeight: 700 }}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={deletingId !== null}
                        startIcon={deletingId !== null ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                        sx={{ fontWeight: 800 }}
                    >
                        {deletingId !== null ? t('Deleting...') : t('Delete Book')}
                    </Button>
                </DialogActions>
            </Dialog>

            {selectedForShare && (
                <ShareLedgerDialog
                    open={shareOpen}
                    onClose={() => setShareOpen(false)}
                    ledgerId={selectedForShare.id}
                    ledgerName={selectedForShare.name}
                />
            )}
        </>
    );
};

export default ManageWorkspaceDialog;
